import { ChangeEvent, Dispatch, FunctionComponent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { InputField } from "@/components/InputField/InputField";
import { AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { CustomColumnTypes, ListSourceType } from "@/enum/list.enum";
import {
	CreateMessageTemplate,
	CreateMessageTemplateSchema,
} from "@/pages/Private/pages/Messages/schema/message-template.schema";
import { useCreateMessageTemplateMutation } from "@/pages/Private/redux/messageTemplate/messageTemplate.api";

interface CreateReplyTemplateModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const typeOptions: AutoCompleteItem[] = Object.values(CustomColumnTypes).map((type) => ({
	id: type,
	title: type,
}));

const sourceTypeOptions: AutoCompleteItem[] = Object.values(ListSourceType).map((type) => ({
	id: type,
	title: type,
}));

export const CreateReplyTemplateModal: FunctionComponent<CreateReplyTemplateModalProps> = ({
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();
	const [createReplyTemplate, { isLoading, error }] = useCreateMessageTemplateMutation();

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<CreateMessageTemplate>({
		defaultValues: {
			name: "",
			content: "",
		},
		resolver: zodResolver(CreateMessageTemplateSchema),
	});

	const formValues = watch();

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateMessageTemplate) => {
		try {
			await createReplyTemplate(values).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={"Create Prompt Template"}
		>
			<div>
				<div className="flex items-center mb-1">
					<InputField
						error={!!errors.name?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setValue("name", e.target.value);
						}}
						label={"Name"}
						name={"name"}
						placeholder={"e.g. My Prompt Template"}
						value={formValues.name || ""}
					/>
				</div>

				<div className="flex items-center mb-1">
					<InputField
						multiline
						error={!!errors.name?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setValue("content", e.target.value);
						}}
						label={"Message"}
						name={"content"}
						placeholder={"e.g. My Prompt Template"}
						rows={4}
						value={formValues.content}
					/>
				</div>

				<div className="flex items-center mb-1"></div>

				{renderErrorMessages(getAllErrors(error, formErrors))}
			</div>
		</Modal>
	);
};
